import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const featuredMovies = movies.filter((m) => m.isHot).slice(0, 4);

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % featuredMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const movie = featuredMovies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-50"></div>

      {featuredMovies.map((m, i) => (
        <div
          key={m.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={m.poster}
            alt={m.title}
            className="w-full h-full object-cover object-top"
            width="1920"
            height="600"
          />
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 w-full">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-950">
                {movie.rating}
              </span>
              <span className="text-xs text-foreground-300">{movie.year}</span>
              <span className="text-xs text-foreground-300">{movie.duration}</span>
              {movie.isNew && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-accent-500 text-foreground-50">
                  NEW
                </span>
              )}
            </div>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
              {movie.title}
            </h2>
            <p className="text-sm text-foreground-300 leading-relaxed line-clamp-2 mb-4">
              {movie.description}
            </p>
            <div className="flex items-center gap-2 flex-wrap mb-5">
              {movie.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 rounded-full text-xs border border-foreground-300/30 text-foreground-300">
                  {tag}
                </span>
              ))}
            </div>
            <Link
              to={`/article/${movie.id}`}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary-500 text-background-950 text-sm font-semibold hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-fill"></i>
              立即观看
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {featuredMovies.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              i === current ? 'w-6 bg-primary-500' : 'bg-foreground-400/50 hover:bg-foreground-300/70'
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          ></button>
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + featuredMovies.length) % featuredMovies.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-950/50 flex items-center justify-center text-foreground-200 hover:bg-background-950/80 transition-colors"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % featuredMovies.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-950/50 flex items-center justify-center text-foreground-200 hover:bg-background-950/80 transition-colors"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </section>
  );
}